import AppStore from '@v4/store/appStore'
import { useCallback, useEffect, useState } from 'react'
import { useSectionId } from '@v4/components/common/SectionItem'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import useFieldFocus from '@v4/hooks/useFieldFocus'
import useEditorMeta from '@v4/hooks/useEditorMeta'
import Tracking from '@v4/utils/tracking'
import { TrackingEvents } from '@v4/constants/tracking'

export const useSingUpResult = ({ metaData }) => {
	const prompt = metaData.prompt?.toBoolean()
	const required = metaData.required?.toBoolean()
	const sectionId = useSectionId()
	const activeSectionId = PageBuilderSelector.useActiveSectionId()
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const { setActiveSectionId } = PageBuilderSelector.useActions()
	const signUpRequired = AppStore.useStoreState(state => state.result.data.sing_up_required)
	const [visited, setVisited] = useState(false)
	const [showed, setShowed] = useState(!prompt)
	const { setBlocked } = AppStore.useStoreActions(state => state.result)
	const blocked = !!signUpRequired && (required || !showed)

	const handleClose = useCallback(
		event => {
			event.preventDefault()
			event.stopPropagation()
			setActiveSectionId({ id: null })
			if (!isBuilderMode) {
				setVisited(true)
				setShowed(true)
			}
		},
		[isBuilderMode, setActiveSectionId],
	)

	const handleSuccess = useCallback(() => setActiveSectionId({ id: null }), [setActiveSectionId])
	const { handleFocus: handleTitleClick } = useFieldFocus({ metaData: metaData.title })
	const { handleFocus: handleButtonClick } = useFieldFocus({ metaData: metaData.buttonText })

	const { editable: titleEditable, handleChange: handleTitleChange } = useEditorMeta({
		metaData: metaData.title,
		key: 'title',
	})

	const { editable: buttonTextEditable, handleChange: handleButtonTextChange } = useEditorMeta({
		metaData: metaData.buttonText,
		key: 'buttonText',
	})

	const open = isBuilderMode ? sectionId === activeSectionId : blocked || sectionId === activeSectionId

	useEffect(() => {
		setBlocked(blocked)
	}, [blocked, setBlocked])

	useEffect(() => {
		if (!isBuilderMode && open) {
			Tracking.track(TrackingEvents.LEAD_FORM_MODAL_OPENED)
		}
	}, [open, isBuilderMode])

	return [
		{
			placeholderColor: metaData.placeholderColor?.metaValue,
			buttonText: metaData.buttonText?.metaValue,
			title: metaData.title?.metaValue,
			titleConfig: metaData.title?.config,
			closable: !required,
			blurred: !visited,
			open,
			titleEditable,
			buttonTextEditable,
			buttonTextConfig: metaData.buttonText?.config,
			titleKey: metaData.title?.tierKey,
			buttonTextKey: metaData.buttonText?.tierKey,
		},
		{
			handleClose: !isBuilderMode && required ? undefined : handleClose,
			handleSuccess,
			handleTitleClick,
			handleButtonClick,
			handleTitleChange,
			handleButtonTextChange,
		},
	]
}
